/*! Shared native-video caption attacher (LAG/LAC themes).
 *
 *  Why this exists: Shopify Files serve .vtt as application/octet-stream + nosniff, and a
 *  cross-origin <track> additionally needs the <video> to be crossorigin — so a track that
 *  points straight at a cdn.shopify.com .vtt silently yields zero cues (readyState 3). The fix:
 *  fetch the VTT ourselves (cdn.shopify.com sends Access-Control-Allow-Origin: *) and hand the
 *  track a SAME-ORIGIN text/vtt blob URL.
 *
 *  Every native-video surface in the theme must attach captions through this helper — never set
 *  track.src to a cdn.shopify.com URL directly.
 *
 *  API: window.LAGVideoCaptions.attach(videoEl, vttUrl)  — replaces any existing tracks; no-op on
 *  empty url. window.LAGVideoCaptions.objectUrl(url) -> Promise<blobUrl> if you need the raw blob.
 *
 *  Auto-attach: any element carrying data-subtitle-url that resolves to a <video> is captioned
 *  automatically — server-rendered surfaces (banner, quickview, any future section) need only emit
 *  data-subtitle-url; no bespoke JS. Surfaces whose player is built later by their own script
 *  (product see-it-in-action, promo-video-row) put data-subtitle-url on a NON-video trigger and
 *  call attach() themselves — the observer finds no <video> under those and skips, so the two
 *  paths never collide.
 */
(function () {
  if (window.LAGVideoCaptions) return; // idempotent — safe to include from multiple sections

  var cache = {};

  function objectUrl(url) {
    if (cache[url]) return Promise.resolve(cache[url]);
    return fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('VTT fetch failed: ' + r.status);
        return r.text();
      })
      .then(function (text) {
        cache[url] = URL.createObjectURL(new Blob([text], { type: 'text/vtt' }));
        return cache[url];
      });
  }

  function attach(videoEl, url) {
    if (!videoEl) return;
    videoEl.querySelectorAll('track').forEach(function (t) { t.remove(); });
    if (!url) return;
    objectUrl(url)
      .then(function (src) {
        var track = document.createElement('track');
        track.kind = 'captions';
        track.label = 'English';
        track.srclang = 'en';
        track.src = src;
        track.default = true;
        videoEl.appendChild(track);
        if (videoEl.textTracks && videoEl.textTracks.length) {
          videoEl.textTracks[0].mode = 'showing';
        }
      })
      .catch(function (e) { console.warn('Captions unavailable for this video:', e); });
  }

  // --- Auto-attach for server-rendered <video data-subtitle-url> (and wrappers of one) ---
  function attachFromEl(el) {
    var url = el.getAttribute('data-subtitle-url');
    if (!url) return;
    var video = el.tagName === 'VIDEO' ? el : el.querySelector('video');
    if (!video || video.dataset.captionsAttached === '1') return;
    video.dataset.captionsAttached = '1';
    attach(video, url);
  }

  function autoAttach(root) {
    if (!root || root.nodeType !== 1) return;
    if (root.matches && root.matches('[data-subtitle-url]')) attachFromEl(root);
    if (root.querySelectorAll) {
      root.querySelectorAll('[data-subtitle-url]').forEach(attachFromEl);
    }
  }

  function initAuto() {
    autoAttach(document.body || document.documentElement);
    if (typeof MutationObserver === 'undefined') return;
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (n) { autoAttach(n); });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAuto);
  } else {
    initAuto();
  }

  window.LAGVideoCaptions = { attach: attach, objectUrl: objectUrl };
})();
